import { useState, useEffect, useCallback } from 'react';

/**
 * 响应式参数Hook - 监听initialParams变化并同步内部状态
 * 标准模式，所有业务hook都应遵循此模式，避免参数状态被锁定在初始值
 *
 * @template T 参数类型
 * @param {T} initialParams 初始参数
 * @param {(params: T) => void} [onParamsChange] 参数变化回调函数
 * @returns {Object} 响应式参数管理对象
 * @property {T} params - 当前参数状态
 * @property {(params: T) => void} setParams - 直接设置参数
 * @property {(newParams: Partial<T>) => T} updateParams - 更新部分参数（自动合并）
 * @property {() => void} resetParams - 重置参数到初始值
 */
export const useResponsiveParams = <T>(
  initialParams: T,
  onParamsChange?: (params: T) => void
) => {
  const [params, setParams] = useState<T>(initialParams);

  // 监听initialParams变化，同步内部状态
  // 移除自动重置，避免与用户操作产生竞态条件
  // 父组件可通过resetParams方法显式重置
  // useEffect(() => {
  //   setParams(initialParams);
  // }, [initialParams]);

  // 参数更新（合并更新，触发回调）
  const updateParams = useCallback(
    (newParams: Partial<T>) => {
      // 使用函数式更新避免闭包问题，同时返回计算出的更新值
      let updatedValue: T | null = null;
      setParams(prev => {
        // 创建更新后的参数对象，过滤掉值为undefined的键（表示删除）
        const updated = { ...prev };
        Object.entries(newParams).forEach(([key, value]) => {
          if (value === undefined) {
            // 删除该键
            delete updated[key as keyof T];
          } else {
            // 更新值
            updated[key as keyof T] = value as T[keyof T];
          }
        });
        updatedValue = updated;
        onParamsChange?.(updated);
        return updated;
      });
      // 注意：由于setState是异步的，这里返回的是计算出的值，可能与实际状态略有延迟
      // 但对于大多数用例（如同步URL参数）是足够的
      return updatedValue as T;
    },
    [onParamsChange] // 移除params依赖，避免闭包问题
  );

  // 参数重置（恢复到初始值，触发回调）
  const resetParams = useCallback(() => {
    setParams(initialParams);
    onParamsChange?.(initialParams);
  }, [initialParams, onParamsChange]);

  return {
    params,
    setParams,
    updateParams,
    resetParams,
  };
};